import { named, withDependencies } from '@wix/thunderbolt-ioc'
import {
	ComponentsStylesOverridesSymbol,
	EditorFeatureConfigSymbol,
	FeatureStateSymbol,
	IComponentsStylesOverrides,
	IPageWillMountHandler,
	IStructureStore,
	Structure,
} from '@wix/thunderbolt-symbols'
import type { IFeatureState } from 'thunderbolt-feature-state'
import type { CompsWithOpacity, IViewerManagerImplementor } from 'thunderbolt-viewer-manager-types'
import { MAX_Z_INDEX, WRICH_TEXT_COMP_TYPE } from './constants'
import { name } from './symbols'
import type { RealTimeConfigEditorConfig, RealTimeConfigState } from './types'
import { getFullId, getRefCompIdFromInflatedId as getRefCompId } from '@wix/thunderbolt-commons'
import { MeasuresApiSymbol } from 'ds-feature-measures-api'
import type { IMeasuresApi } from '@wix/thunderbolt-becky-types'

const getHiddenCompsKey = (compId: string, viewMode: string) => `${viewMode}.${compId}`

const getOverrideStyles = (comps: Array<string>, styleAttribute: string, styleValue: any) =>
	comps.reduce(
		(styles, compId) => ({
			...styles,
			[compId]: { [styleAttribute]: styleValue },
		}),
		{}
	)

const viewerManagerRealTimeConfigApiFactory = (
	featureConfig: RealTimeConfigEditorConfig,
	structureStore: IStructureStore,
	componentsStylesOverrides: IComponentsStylesOverrides,
	featureState: IFeatureState<RealTimeConfigState>,
	measuresApi: IMeasuresApi
): IViewerManagerImplementor<'realTimeConfig'> => {
	featureState.update(() => ({
		showOnTopComps: [],
		maxZIndexCompIds: [],
		opacityComps: { compIds: [], opacity: 1 },
		hideTextComp: '',
		hiddenComps: {},
	}))
	return {
		realTimeConfig: {
			setCompsToShowOnTop: (compIds: Array<string> = []) => {
				const nextMaxZIndexCompIds = compIds.map((compId) => {
					const compPinnedLayerId = `${getRefCompId(compId)}-pinned-layer`
					const isCompInPinnedLayer = structureStore.get(compPinnedLayerId)
					return isCompInPinnedLayer ? compPinnedLayerId : measuresApi.getCompIdSelectorForMesh(compId)
				})
				// reset existing style and add style to the new comp
				componentsStylesOverrides.update({
					...getOverrideStyles(featureState.get().maxZIndexCompIds, 'z-index', null),
					...getOverrideStyles(nextMaxZIndexCompIds, 'z-index', MAX_Z_INDEX),
				})
				featureState.update((state) => ({
					...state,
					showOnTopComps: compIds,
					maxZIndexCompIds: nextMaxZIndexCompIds,
				}))
			},
			getCompsToShowOnTop: () => {
				return featureState.get().showOnTopComps
			},
			setCompsToShowWithOpacity: (value: CompsWithOpacity) => {
				if (!value) {
					console.error('setCompsToShowWithOpacity was called with undefined value')
					value = { compIds: [], opacity: 1 }
				}
				const compsWithOpacity: CompsWithOpacity = { compIds: value.compIds || [], opacity: value.opacity || 1 }
				// reset existing style and add style to the new comp
				componentsStylesOverrides.update({
					...getOverrideStyles(featureState.get().opacityComps.compIds, 'opacity', null),
					...getOverrideStyles(compsWithOpacity.compIds, 'opacity', compsWithOpacity.opacity),
				})
				featureState.update((state) => ({ ...state, opacityComps: compsWithOpacity }))
			},
			getCompsToShowWithOpacity: () => {
				return featureState.get().opacityComps
			},
			setHideTextComponent: (compId: string = '') => {
				let styles = {}
				if (featureState.get().hideTextComp) {
					styles = getOverrideStyles([featureState.get().hideTextComp], 'visibility', null)
					featureState.update((state) => ({ ...state, hideTextComp: compId }))
				}

				const compStructure = compId && structureStore.get(getFullId(compId))
				if (compStructure && compStructure.componentType === WRICH_TEXT_COMP_TYPE) {
					styles = { ...styles, ...getOverrideStyles([compId], 'visibility', 'hidden !important') }
					featureState.update((state) => ({ ...state, hideTextComp: compId }))
				}

				if (Object.keys(styles).length > 0) {
					componentsStylesOverrides.update(styles)
				}
			},
			getHiddenTextComponent: () => {
				return featureState.get().hideTextComp
			},
			setHiddenComp: (compId: string, viewMode: string, isHidden: boolean) => {
				if (featureConfig.isEditorMode) {
					if (viewMode.toLowerCase() === featureConfig.viewMode.toLowerCase()) {
						componentsStylesOverrides.update({
							...getOverrideStyles([compId], 'visibility', isHidden ? 'hidden !important' : null),
						})
					}
				}
				featureState.update((state) => ({
					...state,
					hiddenComps: {
						...state.hiddenComps,
						[getHiddenCompsKey(compId, viewMode.toLowerCase())]: isHidden,
					},
				}))
			},
			getIsCompHidden: (compId: string, viewMode: string) => {
				return featureState.get().hiddenComps[getHiddenCompsKey(compId, viewMode.toLowerCase())] || false
			},
		},
	}
}

export const ViewerManagerRealTimeConfigApi = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		Structure,
		ComponentsStylesOverridesSymbol,
		named(FeatureStateSymbol, name),
		MeasuresApiSymbol,
	],
	viewerManagerRealTimeConfigApiFactory
)

export const RealTimeConfigApi = withDependencies(
	[named(EditorFeatureConfigSymbol, name), ComponentsStylesOverridesSymbol, named(FeatureStateSymbol, name)],
	(
		featureConfig: RealTimeConfigEditorConfig,
		componentsStylesOverrides: IComponentsStylesOverrides,
		featureState: IFeatureState<RealTimeConfigState>
	): IPageWillMountHandler => {
		return {
			name: 'viewerManagerRealTimeConfigApi',
			pageWillMount() {
				const hiddenComps = featureState.get().hiddenComps
				const filteredHiddenComps = Object.keys(hiddenComps).filter((comp) => hiddenComps[comp])
				const currentViewModeHiddenComps = filteredHiddenComps
					.filter((comp) => comp.startsWith(featureConfig.viewMode.toLowerCase()))
					.map((comp) => comp.split('.')[1])
				const previousViewModeHiddenComps = filteredHiddenComps
					.filter((comp) => !comp.startsWith(featureConfig.viewMode.toLowerCase()))
					.map((comp) => comp.split('.')[1])
				// feature active only in editor mode - if isPreview show all hidden components
				const visibility = featureConfig.isEditorMode ? 'hidden' : null
				componentsStylesOverrides.update({
					...getOverrideStyles(currentViewModeHiddenComps, 'visibility', visibility),
					...getOverrideStyles(previousViewModeHiddenComps, 'visibility', null),
				})
			},
		}
	}
)
